import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuProps,
  formatDate,
  Icons,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/lib/routes";
import { getScheduleDetailsLink } from "@/utils/scheduleUtils";

import { DeleteEventMenuItem } from "../Complexes/SingleField/DeleteEventMenuItem";
import { useDeleteEvent } from "../Complexes/SingleField/FieldSchedule/scheduleMutations.queries";
import { MoveEventDialog } from "../Complexes/SingleField/MoveEventDialog/MoveEventDialog";
import { Source } from "../Complexes/SingleField/Source";
import { EventScheduleRow } from "./EventSchedule.hooks";

function formatRowDate(date: Date): string {
  if (isNaN(date.getTime())) return "-";
  return `${formatDate(date, "MMM d")} · ${formatDate(date, "h:mm a")}`;
}

function RowActions({ row }: { row: EventScheduleRow }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const navigate = useNavigate();
  const confirm = useConfirmStore((s) => s.confirm);
  const { deleteEvent } = useDeleteEvent();

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Confirm Event Deletion",
      description: "Are you sure you want to remove this event?",
      confirmText: "Delete Event",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;
    useConfirmStore.setState({ isPending: true });
    try {
      await deleteEvent(row.raw);
    } finally {
      useConfirmStore.setState({ isOpen: false, isPending: false });
    }
  };

  const detailsLink = getScheduleDetailsLink(row.raw, row.currentGameUuid);
  const detailsItems: DropdownMenuProps["items"] = detailsLink
    ? [
        {
          label: detailsLink.label,
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            const path = ROUTES.EDIT_EVENT.replace(
              ":complexName",
              row.complexName,
            )
              .replace(":fieldId", row.machineUuid)
              .replace(":eventId", detailsLink.eventId);
            navigate(path);
          },
        },
      ]
    : [];

  const items: DropdownMenuProps["items"] = [
    ...detailsItems,
    {
      label: "Move Fields",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsMoveOpen(true);
      },
    },
    <DeleteEventMenuItem
      key="delete-event"
      schedule={row.raw}
      onDelete={handleDelete}
    />,
  ];

  return (
    <>
      <DropdownMenu
        modal={false}
        variant="light"
        open={isOpen}
        onOpenChange={setIsOpen}
        align="end"
        items={items}
        onClick={(e) => e.stopPropagation()}
        trigger={
          <Button
            className={cn(isOpen && "bg-[hsla(218,100%,52%,0.1)]")}
            variant="ghost"
            size="icon"
          >
            <Icons.EllipsisVertical className="text-blue size-7!" />
          </Button>
        }
      />
      <MoveEventDialog
        event={row.raw}
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
      />
    </>
  );
}

export const eventScheduleColumns: ColumnDef<EventScheduleRow>[] = [
  {
    id: "event",
    accessorKey: "name",
    header: "Event",
    enableSorting: true,
    cell: ({ row: { original: row } }) => (
      <div className="flex flex-col">
        <span className="text-sm">{row.name}</span>
        <span className="text-xs text-gray-400">
          {formatRowDate(row.startTime)}
        </span>
      </div>
    ),
  },
  {
    id: "location",
    accessorKey: "fieldName",
    header: "Location",
    enableSorting: true,
    cell: ({ row: { original: row } }) => (
      <div className="flex flex-col min-w-0">
        <span className="text-sm truncate">{row.fieldName}</span>
        <span className="text-xs text-gray-400 truncate">
          {row.complexName}
        </span>
      </div>
    ),
  },
  {
    id: "source",
    header: "Source",
    size: 80,
    enableSorting: false,
    cell: ({ row: { original: row } }) =>
      row.source ? (
        <Source source={row.source} />
      ) : (
        <span className="text-sm text-gray-400">--</span>
      ),
  },
  {
    id: "actions",
    header: "",
    size: 60,
    enableSorting: false,
    meta: { headerClassName: "justify-end" },
    cell: ({ row: { original: row } }) => (
      <div className="flex justify-end">
        <RowActions row={row} />
      </div>
    ),
  },
];
